import { Add } from '@mui/icons-material';
import { Button, FormControl, InputLabel, MenuItem, Select, SelectProps } from '@mui/material';
import React, { ReactNode } from 'react';
import { useController, useFormContext, RegisterOptions } from 'react-hook-form';

type Props<T extends string> = SelectProps & {
  name: string;
  label?: string;
  defaultValue?: string;
  rules?: RegisterOptions;
  options: T[];
  renderOption?: (option: T) => ReactNode;
  onAddNew?: () => void;
  addButtonText?: string;
};

const FormSelect = <T extends string>({
  name,
  label,
  defaultValue = '',
  rules,
  options,
  renderOption,
  sx,
  onAddNew,
  addButtonText,
  color = 'secondary',
  ...rest
}: Props<T>) => {
  const { field } = useController({ name, defaultValue, rules });
  const {
    formState: { errors },
  } = useFormContext<{ [key: string]: string }>();
  const error = errors[name];

  return (
    <FormControl fullWidth sx={sx} error={!!error}>
      <InputLabel color={color}>{label}</InputLabel>
      <Select
        id={name}
        label={label}
        fullWidth
        color={color}
        sx={{
          div: {
            backgroundColor: (t) => t.palette.background.paper,
          },
        }}
        {...rest}
        {...field}
        value={field.value || ''}
      >
        {options.map((option) => (
          <MenuItem key={option} value={option}>
            {renderOption ? renderOption(option) : option}
          </MenuItem>
        ))}
        {onAddNew && (
          <Button startIcon={<Add />} fullWidth onClick={onAddNew}>
            {addButtonText}
          </Button>
        )}
      </Select>
    </FormControl>
  );
};

export default FormSelect;
